import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  memo,
} from "react";
import {
  StyleSheet,
  Animated,
  ScrollView,
  Platform,
  View,
  useWindowDimensions,
  TouchableOpacity,
} from "react-native";

import CustomLinearGradient, {
  GRADIENT_TYPE,
} from "../../../components/CustomLinearGradient";
import { usePlatform } from "../../../providers/PlatformProvider";
import { TabsHeader } from "./TabsHeader";
import { useActiveArticleStore } from "../../../store/activeArticleStore";
import { FULL_HEIGHT } from "../../../shared/constants/dimension";
import { useThemeSettings } from "../../../ThemeProvider";
import {
  ACTION_TYPE,
  ANALYTICS_EVENT,
  DIRECTION,
} from "../../../analytics/types";
import {
  trackInteraction,
  trackScreenTime,
} from "../../../analytics/analyticsUtil";
import { ROUTES } from "../../../shared/constants/routes";
import { CustomIcon } from "../../../components/CustomIcon";

const CARD_SPACING = 16;
const IDLE_FRAMES = 3;

/* ------------------------------ Carousel Card ----------------------------- */

const CarouselCardComponent = ({
  item,
  isActive,
  width,
  showWebVersion,
  cardRef,
  titleComponent,
  setVerticalGestureDisabled,
}: any) => {
  const { theme } = useThemeSettings();

  const verticalScrollViewRef = useRef(null);
  const timerRef = useRef(null);

  const [showScrollIndicator, setShowScrollIndicator] = useState(undefined);
  const [containerHeight, setContainerHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [hasScroll, setHasScroll] = useState(false);

  const onToggleScrollIndicator = useCallback(() => {
    const shouldLockVertical =
      showScrollIndicator === "down_arrow" || showScrollIndicator == null;
    setVerticalGestureDisabled(shouldLockVertical);

    setShowScrollIndicator((prev) =>
      prev !== "up_arrow" ? "up_arrow" : "down_arrow"
    );
  }, [showScrollIndicator, setVerticalGestureDisabled]);

  useEffect(() => {
    if (!isActive) {
      setShowScrollIndicator(undefined);
      setVerticalGestureDisabled(false);
    }
  }, [isActive, setVerticalGestureDisabled]);

  useEffect(() => {
    if (!isActive) return;

    requestAnimationFrame(() => {
      setHasScroll(contentHeight > containerHeight);
    });
  }, [isActive, containerHeight, contentHeight]);

  useEffect(() => {
    if (!hasScroll) return;

    const scroll = verticalScrollViewRef.current;
    if (!scroll) return;

    if (showScrollIndicator === "up_arrow") {
      scroll.scrollTo({ y: 200, animated: true });
    }

    if (timerRef.current) clearTimeout(timerRef.current);

    // timerRef.current = setTimeout(() => {
    //   scroll.scrollTo({ y: 0, animated: true });
    // }, 350);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [hasScroll, showScrollIndicator]);

  return (
    <Animated.View
      ref={cardRef}
      style={[
        styles.card,
        { width, height: FULL_HEIGHT },
        Platform.OS === "web" && { scrollSnapAlign: "center" },
      ]}
    >
      <CustomLinearGradient gradientType={GRADIENT_TYPE.BASE_WEB}>
        <View
          style={[
            styles.innerCard,
            !showWebVersion && { marginTop: titleComponent ? 70 : 0 },
            !isActive && styles.inactiveCard,
          ]}
        >
          {item.key === "GIST" ? (
            item.content
          ) : (
            <ScrollView
              ref={verticalScrollViewRef}
              scrollEnabled={
                Platform.OS === "web" ||
                (hasScroll && showScrollIndicator === "up_arrow")
              }
              style={styles.cardScroll}
              contentContainerStyle={styles.cardContent}
              showsVerticalScrollIndicator={false}
              onLayout={(e) =>
                setContainerHeight(e.nativeEvent.layout.height)
              }
              onContentSizeChange={(_, h) => setContentHeight(h)}
            >
              {item.content}
            </ScrollView>
          )}
        </View>

        {!showWebVersion && titleComponent && (
          <View
            style={[
              styles.nativeTitle,
              { backgroundColor: theme.colors.surfaceBg },
              !isActive && styles.inactiveCard,
            ]}
          >
            {titleComponent}
          </View>
        )}

        {Platform.OS !== "web" && hasScroll && (
          <TouchableOpacity
            activeOpacity={0.85}
            onPress={onToggleScrollIndicator}
            style={[
              styles.floatingButton,
              { backgroundColor: theme.colors.primary },
            ]}
          >
            <CustomIcon
              icon={showScrollIndicator ?? "down_arrow"}
              size={25}
              color={theme.colors.surfaceColor}
            />
          </TouchableOpacity>
        )}
      </CustomLinearGradient>
    </Animated.View>
  );
};

const CarouselCard = memo(
  CarouselCardComponent,
  (prev, next) =>
    prev.isActive === next.isActive &&
    prev.item === next.item &&
    prev.width === next.width &&
    prev.showWebVersion === next.showWebVersion
);

/* --------------------------- Stacked Carousel ---------------------------- */

export default function StackedCarousel({
  cards,
  titleComponent,
  setVerticalGestureDisabled,
}) {
  const scrollRef = useRef(null);
  const cardRefs = useRef({});
  const rafRef = useRef(null);

  const lastX = useRef(0);
  const lastFrameX = useRef(0);
  const idleCount = useRef(0);
  const scrollingRef = useRef(false);

  const { width } = useWindowDimensions();
  const { showWebVersion } = usePlatform();
  const { activeView, version, setActiveArticleView } =
    useActiveArticleStore();

  const MAX_WIDTH = Math.min(width, 650);
  const CARD_WIDTH = MAX_WIDTH - CARD_SPACING * (showWebVersion ? 8 : 4);
  const SNAP = CARD_WIDTH + CARD_SPACING;
  const SIDE_PEEK = (MAX_WIDTH - CARD_WIDTH) / 2;

  const removeTrailingNumber = (str) => str.replace(/_\d+$/, "");

  /* ------------------------ Track screen time --------------------------- */

  useEffect(() => {
    const start = Date.now();

    return () => {
      const durationMs = Date.now() - start;
      trackScreenTime(removeTrailingNumber(activeView), durationMs);
    };
  }, [activeView]);

  /* ------------------------- Compute index ------------------------- */

  const indexFromOffset = useCallback(
    (x) =>
      Math.min(cards.length - 1, Math.max(0, Math.round((x - SIDE_PEEK) / SNAP))),
    [SNAP, SIDE_PEEK, cards.length]
  );

  /* ------------------------- Commit index ------------------------- */

  const commitIndex = useCallback(
    (index) => {
      const change_to = cards[index]?.key;

      if (!change_to || change_to === activeView) return;

      const previousView = activeView;

      const direction =
        previousView && change_to.toLowerCase().includes("storysofar")
          ? DIRECTION.RIGHT
          : DIRECTION.LEFT;

      setActiveArticleView(change_to);

      trackInteraction({
        event: ANALYTICS_EVENT.INTERACTION,
        screen_name: ROUTES.IMMERSRSIVE_NEWS_ARTICLE,
        action: ACTION_TYPE.SWIPE,
        direction,
        component: "StoryView",
        context: {
          active_view: removeTrailingNumber(previousView),
          change_to: removeTrailingNumber(change_to),
        },
      });
    },
    [cards, activeView, setActiveArticleView]
  );

  /* ------------------------- Scroll to index ------------------------- */

  const scrollToIndex = useCallback(
    (index, animated = true) => {
      if (index < 0 || index >= cards.length) return;

      if (Platform.OS === "web") {
        cardRefs.current[cards[index].key]?.scrollIntoView({
          behavior: animated ? "smooth" : "auto",
          inline: "center",
        });
      } else {
        scrollingRef.current = true;

        scrollRef.current?.scrollTo({
          x: index * SNAP,
          animated,
        });

        setTimeout(() => {
          scrollingRef.current = false;
        }, 300);
      }
    },
    [cards, SNAP]
  );

  /* ------------------------- Sync external view ------------------------- */

  useEffect(() => {
    const index = cards.findIndex((c) => c.key === activeView);

    if (index >= 0) scrollToIndex(index, Platform.OS !== "web");
  }, [activeView, version, cards, scrollToIndex]);

  /* ------------------------- Momentum end ------------------------- */

  const onMomentumEnd = useCallback(
    (e) => {
      if (Platform.OS === "web" || scrollingRef.current) return;

      const index = indexFromOffset(e.nativeEvent.contentOffset.x);

      commitIndex(index);
    },
    [indexFromOffset, commitIndex]
  );

  /* --------------------------- Web scroll detection ----------------------- */

  const onScroll = useCallback(
    (e) => {
      if (Platform.OS !== "web") return;

      lastX.current = e.nativeEvent.contentOffset.x;

      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      idleCount.current = 0;

      const detectIdle = () => {
        const delta = Math.abs(lastX.current - lastFrameX.current);

        lastFrameX.current = lastX.current;

        if (delta < 1) idleCount.current++;
        else idleCount.current = 0;

        if (idleCount.current >= IDLE_FRAMES) {
          const index = indexFromOffset(lastX.current);

          commitIndex(index);
          scrollToIndex(index);

          rafRef.current = null;
          return;
        }

        rafRef.current = requestAnimationFrame(detectIdle);
      };

      rafRef.current = requestAnimationFrame(detectIdle);
    },
    [indexFromOffset, commitIndex, scrollToIndex]
  );

  /* ------------------------- Tab click ------------------------- */

  const onTabPress = useCallback(
    (key) => {
      const index = cards.findIndex((c) => c.key === key);

      if (index < 0 || key === activeView) return;

      commitIndex(index);
      scrollToIndex(index);
    },
    [cards, commitIndex, scrollToIndex, activeView]
  );

  /* ------------------------------ Render --------------------------------- */

  return (
    <View style={{ flex: 1 }}>
      {showWebVersion && <View style={styles.webTitle}>{titleComponent}</View>}

      <TabsHeader tabs={cards} activetab={activeView} setActiveTab={onTabPress} />

      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        snapToInterval={Platform.OS !== "web" ? SNAP : undefined}
        snapToAlignment="start"
        decelerationRate="fast"
        removeClippedSubviews
        onMomentumScrollEnd={onMomentumEnd}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[
          { paddingHorizontal: SIDE_PEEK },
          Platform.OS === "web" && { scrollSnapType: "x mandatory" },
        ]}
        style={[styles.carousel, {width : MAX_WIDTH}, Platform.OS === 'web' ? {paddingVertical: 5} : {paddingVertical: 10}]}
      >
        {cards.map((item) => (
          <CarouselCard
            key={item.key}
            item={item}
            width={CARD_WIDTH}
            isActive={item.key === activeView}
            showWebVersion={showWebVersion}
            setVerticalGestureDisabled={setVerticalGestureDisabled}
            titleComponent={
              Platform.OS === "web" && item.key === "GIST"
                ? titleComponent
                : null
            }
            cardRef={
              Platform.OS === "web"
                ? (el) => (cardRefs.current[item.key] = el)
                : undefined
            }
          />
        ))}
      </ScrollView>
    </View>
  );
}

/* ------------------------------ Styles ---------------------------------- */

const styles = StyleSheet.create({
  carousel: {
    alignSelf: "center",
  },

  card: {
    borderRadius: 18,
    marginRight: CARD_SPACING,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOpacity: 0.15,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 6,
  },

  innerCard: {
    height: FULL_HEIGHT,
  },

  inactiveCard: {
    opacity: 0.12,
  },

  cardScroll: {
    flex: 1,
    marginHorizontal: 20,
    marginVertical: 16,
    backgroundColor: "transparent",
  },

  cardContent: {
    gap: 16,
  },

  webTitle: {
    position: "absolute",
    top: -50,
    alignSelf: "center",
    fontSize: 28,
  },

  nativeTitle: {
    margin: 8,
    padding: 8,
    borderRadius: 16,
    position: "absolute",
    width: "95%",
    alignSelf: "center",
  },

  floatingButton: {
    position: "absolute",
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: "center",
    alignItems: "center",
    bottom: 1,
    right: 1,
    zIndex: 999,
    elevation: 12,
  },
});